import { KeyValueObj } from "@/common/types";

/**
 * MED-706 — one product line of the create-order payload, built the same way
 * whichever entry point the buyer came through.
 *
 * The New Order page has two: the supplier wizard (make-order/index.tsx) and
 * the catalogue quick-order (orderByProductSearch/all-products-order.tsx). Both
 * POST to ORDERS_URL, and an order for the same product/batch/quantity must
 * produce identical OrderProduct rows regardless of route. They were drifting —
 * different `item_type`, a product-level quantity multiplier on one and a
 * per-batch one intended on the other — so the construction lives here once.
 *
 * The server (OrderManager::OrderProcessor) derives `package_type`,
 * `price_per_unit`, `quantity` and `product_id` itself from the batches'
 * inventory and listing, identically for both routes, so those are sent as
 * inert placeholders rather than computed here — there is nothing to keep in
 * step. What the server DOES take from the payload is `item_type`,
 * `atc_code_id` (drug only), `brand_name`, `product_strength*`, `product_form`,
 * `description`, and — the one that matters — each batch's base-unit `quantity`.
 */

/** One batch the buyer picked, with the amount wanted in the SELLING unit. */
export interface IOrderLineBatch {
  inventory_id: string;
  /** Packages wanted, in this batch's own selling level (cartons, packs, …). */
  amount: number;
  /**
   * Base units one of those packages holds — from `readBatchUnit` on the
   * batch's own `quantity_display`, NOT the product's `base_unit_count`. A
   * product's batches can belong to holders selling at different levels.
   */
  multiplier: number;
}

export interface IBuildOrderLineArgs {
  /** A DrugSerializer product record — same shape from COMPANY_PRODUCTS and ALL_PRODUCTS. */
  product: KeyValueObj;
  batches: IOrderLineBatch[];
}

export function buildOrderLine({ product, batches }: IBuildOrderLineArgs) {
  const payloadBatches = batches.map((batch) => ({
    inventory_id: batch.inventory_id,
    quantity: batch.amount * (batch.multiplier || 1),
  }));

  return {
    // Derived from the product so a medical item is not booked as a drug if
    // this flow is ever extended to them; today every product here is a drug.
    item_type: product?.product_type ?? "drug",
    atc_code_id: product?.atc_code?.id,
    brand_name: product?.brand_name,
    // Server recomputes this from the batch sum; sent for shape parity only.
    quantity: payloadBatches.reduce((sum, batch) => sum + batch.quantity, 0),
    // Server overwrites both from the seller's inventory/listing — see file note.
    package_type: "",
    price_per_unit: 0,
    description: "",
    product_strength: product?.strength,
    product_strength_unit: product?.strength_unit,
    // The serializer field is `form`; `product_form` was always undefined.
    product_form: product?.form,
    batches: payloadBatches,
  };
}
